import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, Sparkles } from "lucide-react";

export default function Hero() {
    return (
        <section
            className="relative min-h-[100vh] flex items-center overflow-hidden"
            data-testid="hero-section"
        >
            {/* Layered background — visible MEP systems imagery */}
            <div className="absolute inset-0">
                <img
                    src="https://images.unsplash.com/photo-1481026469463-66327c86e544?auto=format&fit=crop&w=2400&q=80"
                    alt=""
                    className="w-full h-full object-cover opacity-85"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B1628]/95 via-[#0B1628]/55 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1628]/30 via-transparent to-[#0B1628]" />
                <div className="absolute inset-0 bg-[#0B1628]/10" />
            </div>

            {/* Grid overlay */}
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40 pointer-events-none" />

            {/* Animated glow orbs */}
            <motion.div
                animate={{ x: [0, 50, 0], y: [0, -30, 0] }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full bg-blue-600/25 blur-[130px] pointer-events-none"
            />
            <motion.div
                animate={{ x: [0, -40, 0], y: [0, 40, 0] }}
                transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full bg-cyan-500/15 blur-[110px] pointer-events-none"
            />

            {/* Floating blueprint lines SVG decoration */}
            <svg className="absolute top-20 right-10 w-96 h-96 opacity-20 pointer-events-none hidden lg:block" viewBox="0 0 400 400" fill="none">
                <motion.circle
                    cx="200" cy="200" r="120"
                    stroke="url(#heroRing)" strokeWidth="1" strokeDasharray="4 8" fill="none"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                    style={{ transformOrigin: "200px 200px" }}
                />
                <motion.circle
                    cx="200" cy="200" r="180"
                    stroke="url(#heroRing)" strokeWidth="1" strokeDasharray="2 10" fill="none"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
                    style={{ transformOrigin: "200px 200px" }}
                />
                <circle cx="200" cy="80" r="3" fill="#00E5FF" />
                <circle cx="320" cy="200" r="3" fill="#2563EB" />
                <defs>
                    <linearGradient id="heroRing" x1="0" y1="0" x2="400" y2="400">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#00E5FF" stopOpacity="0.4" />
                    </linearGradient>
                </defs>
            </svg>

            <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-28 w-full">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-5xl"
                >
                    <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 backdrop-blur-md mb-10" data-testid="hero-badge">
                        <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                        <span className="text-xs font-mono tracking-[0.2em] uppercase text-blue-200">24/7 Production · World-Class Talent · Est. 2017</span>
                    </div>

                    <h1 className="font-heading font-black text-white text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.92] tracking-tighter" data-testid="hero-heading">
                        Premium MEP engineering.
                        <br />
                        <span className="relative inline-block">
                            <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-500 bg-clip-text text-transparent">Delivered 24/7.</span>
                            <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 400 10" fill="none">
                                <path d="M0 5 Q 100 0, 200 5 T 400 5" stroke="url(#grad)" strokeWidth="2" fill="none" />
                                <defs>
                                    <linearGradient id="grad" x1="0" y1="0" x2="400" y2="0">
                                        <stop offset="0%" stopColor="#2563EB" />
                                        <stop offset="100%" stopColor="#00E5FF" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </span>
                        <br />
                        <span className="text-slate-300">At pricing that moves projects.</span>
                    </h1>

                    <p className="mt-10 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed" data-testid="hero-subtitle">
                        A decade of MEP design for the world's most demanding environments — powered by
                        active production teams running around the clock. Built for fast-tracked timelines,
                        engineered by the top 1% of global talent, priced to win.
                    </p>

                    <div className="mt-12 flex flex-wrap items-center gap-4">
                        <Link
                            to="/contact"
                            className="group inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-7 py-4 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(37,99,235,0.4)] hover:shadow-[0_0_50px_rgba(37,99,235,0.7)]"
                            data-testid="hero-cta-primary"
                        >
                            Start a Fast-Track Project
                            <ArrowUpRight className="w-5 h-5 transition-transform group-hover:rotate-45" />
                        </Link>
                        <Link
                            to="/projects"
                            className="inline-flex items-center gap-3 bg-white/5 hover:bg-white/10 text-white font-semibold px-7 py-4 rounded-full border border-white/10 backdrop-blur-md transition-all duration-300"
                            data-testid="hero-cta-secondary"
                        >
                            View Our Work
                        </Link>
                    </div>
                </motion.div>

                {/* Bottom marquee stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="absolute bottom-10 left-6 right-6 md:left-10 md:right-10 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6"
                >
                    <div className="flex items-center gap-2 text-slate-500 text-xs font-mono tracking-widest">
                        <ArrowDown className="w-4 h-4 animate-bounce" />
                        <span>SCROLL TO EXPLORE</span>
                    </div>
                    <div className="hidden md:flex items-center divide-x divide-white/10">
                        {[
                            { n: "10+", l: "Years" },
                            { n: "240+", l: "Projects" },
                            { n: "24/7", l: "Production" },
                            { n: "8", l: "Sectors" },
                        ].map((s, i) => (
                            <div key={i} className="px-6 first:pl-0">
                                <div className="font-heading font-black text-white text-2xl">{s.n}</div>
                                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mt-1">{s.l}</div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
